import React from 'react';
import { StyleSheet, TextStyle } from 'react-native';
import Markdown from 'react-native-markdown-display';
import { Theme } from '../../utils/theme';

interface MarkdownRendererProps {
  content: string;
  fontSize?: number;
  fontFamily?: string;
  lineHeight?: number;
}

/**
 * Normalize markdown to ensure proper list rendering
 * Adds blank lines before lists if missing
 */
const normalizeMarkdown = (text: string): string => {
  let normalized = text;
  
  // If text starts with a list item, add a blank line at the start
  if (/^[-*+] /.test(normalized) || /^\d+\. /.test(normalized)) {
    normalized = '\n' + normalized;
  }
  
  // Add blank line before bullet lists if missing
  normalized = normalized.replace(/([^\n])\n([-*+] )/g, '$1\n\n$2');
  
  // Add blank line before numbered lists if missing
  normalized = normalized.replace(/([^\n])\n(\d+\. )/g, '$1\n\n$2');
  
  return normalized;
};

/**
 * Themed Markdown Renderer for book and insight content
 * Uses react-native-markdown-display with our design system theme
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  fontSize = 17,
  fontFamily = Theme.typography.fontFamily.regular,
  lineHeight,
}) => {
  const computedLineHeight = lineHeight || fontSize * 1.6;
  const normalizedContent = normalizeMarkdown(content);

  const markdownStyles = StyleSheet.create({
    body: {
      color: Theme.colors.textPrimary,
      fontSize,
      lineHeight: computedLineHeight,
      fontFamily,
    },
    heading1: {
      color: Theme.colors.textPrimary,
      fontSize: fontSize * 1.5,
      lineHeight: fontSize * 1.5 * 1.3,
      fontFamily: Theme.typography.fontFamily.bold,
      marginTop: Theme.spacing.lg,
      marginBottom: Theme.spacing.md,
    },
    heading2: {
      color: Theme.colors.textPrimary,
      fontSize: fontSize * 1.3,
      lineHeight: fontSize * 1.3 * 1.3,
      fontFamily: Theme.typography.fontFamily.semiBold,
      marginTop: Theme.spacing.md,
      marginBottom: Theme.spacing.sm,
    },
    heading3: {
      color: Theme.colors.textPrimary,
      fontSize: fontSize * 1.15,
      lineHeight: fontSize * 1.15 * 1.3,
      fontFamily: Theme.typography.fontFamily.semiBold,
      marginTop: Theme.spacing.md,
      marginBottom: Theme.spacing.sm,
    },
    paragraph: {
      color: Theme.colors.textPrimary,
      fontSize,
      lineHeight: computedLineHeight,
      fontFamily,
      marginBottom: Theme.spacing.md,
    },
    strong: {
      fontFamily: Theme.typography.fontFamily.semiBold,
      color: Theme.colors.textPrimary,
    },
    em: {
      fontStyle: 'italic' as const,
      color: Theme.colors.textPrimary,
    },
    blockquote: {
      backgroundColor: Theme.colors.surface,
      borderLeftColor: Theme.colors.primary,
      borderLeftWidth: 4,
      paddingLeft: Theme.spacing.md,
      paddingVertical: Theme.spacing.sm,
      marginVertical: Theme.spacing.md,
      borderRadius: Theme.borderRadius.sm,
    },
    code_inline: {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      color: Theme.colors.textPrimary,
      fontFamily: 'Courier',
      fontSize: fontSize * 0.9,
      paddingHorizontal: 4,
      paddingVertical: 2,
      borderRadius: 4,
    },
    code_block: {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      color: Theme.colors.textPrimary,
      fontFamily: 'Courier',
      fontSize: fontSize * 0.9,
      padding: Theme.spacing.md,
      borderRadius: Theme.borderRadius.md,
      marginVertical: Theme.spacing.md,
    },
    fence: {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      color: Theme.colors.textPrimary,
      fontFamily: 'Courier',
      fontSize: fontSize * 0.9,
      padding: Theme.spacing.md,
      borderRadius: Theme.borderRadius.md,
      marginVertical: Theme.spacing.md,
    },
    bullet_list: {
      marginVertical: Theme.spacing.sm,
    },
    ordered_list: {
      marginVertical: Theme.spacing.sm,
    },
    list_item: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: Theme.spacing.xs,
    },
    bullet_list_icon: {
      color: Theme.colors.primary,
      fontSize: fontSize,
      lineHeight: computedLineHeight,
      marginRight: Theme.spacing.sm,
    },
    bullet_list_content: {
      flex: 1,
      color: Theme.colors.textPrimary,
      fontSize,
      lineHeight: computedLineHeight,
      fontFamily,
    },
    ordered_list_icon: {
      color: Theme.colors.primary,
      fontSize: fontSize,
      lineHeight: computedLineHeight,
      marginRight: Theme.spacing.sm,
      fontFamily: Theme.typography.fontFamily.semiBold,
    },
    ordered_list_content: {
      flex: 1,
      color: Theme.colors.textPrimary,
      fontSize,
      lineHeight: computedLineHeight,
      fontFamily,
    },
    link: {
      color: Theme.colors.primary,
      textDecorationLine: 'underline' as const,
    },
    hr: {
      backgroundColor: Theme.colors.border,
      height: 1,
      marginVertical: Theme.spacing.lg,
    },
  });

  return (
    <Markdown style={markdownStyles}>
      {normalizedContent}
    </Markdown>
  );
};

export default MarkdownRenderer;

